import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, CalendarRange } from "lucide-react";
import { api } from "@/api/client";
import type { RollupReportSummary } from "../../../types";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { CreateRollupForm } from "./CreateRollupForm";
import { RollupCard } from "./RollupCard";
import { RollupDetailView } from "./RollupDetailView";
import { AutoRollupCard } from "./AutoRollupCard";

export function RollupsView() {
  const navigate = useNavigate();
  const { rollupId } = useParams<{ rollupId: string }>();
  const [rollups, setRollups] = useState<RollupReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const fetchRollups = async () => {
    setLoading(true);
    try {
      setRollups(await api.rollups.list());
      setError(null);
    } catch (err: any) {
      setError(err?.message || "Failed to load rollups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRollups(); }, []);

  // Poll while any rollup is still generating its narrative.
  useEffect(() => {
    if (rollupId || !rollups.some(r => r.status === "generating")) return;
    const timer = setInterval(fetchRollups, 4000);
    return () => clearInterval(timer);
  }, [rollups, rollupId]);

  if (rollupId) {
    const id = Number(rollupId);
    if (!Number.isInteger(id) || id < 1) return <p className="text-sm text-slate-500">Rollup not found.</p>;
    return <RollupDetailView rollupId={id} onBack={() => { navigate("/app/rollups"); fetchRollups(); }} />;
  }

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this rollup?")) return;
    await api.rollups.delete(id);
    fetchRollups();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button onClick={() => setShowForm(!showForm)}>
          <Plus className="w-4 h-4 mr-1.5" />
          New Rollup
        </Button>
      </div>

      {showForm && (
        <div className="animate-fade-up">
          <CreateRollupForm
            onSuccess={(id) => { setShowForm(false); navigate(`/app/rollups/${id}`); }}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      <AutoRollupCard onChanged={fetchRollups} />

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[1, 2, 3].map(i => <div key={i} className="h-36 bg-slate-100 rounded-lg animate-subtle-pulse" />)}
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={fetchRollups} />
      ) : rollups.length === 0 ? (
        <EmptyState
          icon={CalendarRange}
          title="No rollups yet"
          description="Generate a monthly, quarterly, or yearly recap of how your brands trended across AI answers — built from your tracking history."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {rollups.map(rollup => (
            <RollupCard
              key={rollup.id}
              rollup={rollup}
              onClick={() => navigate(`/app/rollups/${rollup.id}`)}
              onDelete={() => handleDelete(rollup.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
